<template>
  <div class="event-table">
    <el-table
      :data="teammateArray"
      style="width: 100%"
      empty-text="No teammates"
    >
      <el-table-column prop="name" label="Teammate" :width="300">
        <template #default="scope">
          <router-link
            :to="{
              name: 'UserProfile',
              params: { link: scope.row.link },
            }"
          >
            <div class="event-table__user">
              <el-avatar class="event-table__avatar" :size="50">
                <user-avatar :userId="scope.row.userId" />
              </el-avatar>
              <span class="event-table__name">
                {{ scope.row.name }}
              </span>
            </div>
          </router-link>
        </template>
      </el-table-column>
      <el-table-column prop="role" label="Role">
        <template #default="scope">
          <div>
            <el-select
              v-model="scope.row.role"
              @change="requestChangeRole(scope.row.teammateId, scope.row.role)"
              v-if="isAdmin"
            >
              <el-option
                v-for="role in roleDescriptions"
                :key="role.value"
                :label="role.description"
                :value="role.value"
              >
              </el-option>
            </el-select>

            <div class="event-table__role" v-else>
              {{ scope.row.roleDescription }}
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="" :width="110" v-if="isAdmin">
        <template #default="scope">
          <el-button
            plain
            type="danger"
            @click="handleDeleteTeammate(scope.row)"
          >
            Delete
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      v-model="isDeleteTeammateConfirmationVisible"
      width="30%"
      title="Confirmation"
    >
      <span v-if="teammateToDelete">
        Are you sure you want to delete {{ teammateToDelete.name }} from this
        team?
      </span>
      <template #footer>
      <span class="dialog-footer">
        <el-button @click="cancelTeammateDelete">
          Cancel
        </el-button>
        <el-button
          type="danger"
          @click="requestDeleteTeammate(teammateToDelete.teammateId)"
          :disabled="isButtonDisabled('delete-teammate')"
        >
          Delete
        </el-button>
      </span>
    </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  getTeammates,
  getTeamRoleDescriptions,
  changeTeammateRole,
  removeTeammate,
} from '@/api/team.js';

import { showMessage } from '@/utils/messages.js';
import { disableButton } from '@/mixins/disableButton.js';
import UserAvatar from '@/components/profile/UserAvatar.vue'

export default {
  name: 'TeamTeammates',
  components: {
    UserAvatar,
  },

  props: {
    teamId: {
      type: Number,
      required: true,
    },
    isAdmin: {
      type: Boolean,
      required: false,
    },
    isUserLoggedIn: {
      type: Boolean,
      required: false,
    },
  },
  mixins: [disableButton],
  data() {
    return {
      teammateArray: [],
      roleDescriptions: [],
      isDeleteTeammateConfirmationVisible: false,
      teammateToDelete: {},
    };
  },
  methods: {
    showMessage,
    async fetchTeammates() {
      const { teamId } = this;
      const { data, success } = await getTeammates({ teamId });

      if (success) {
        this.setData(data.items);
      }
    },
    async fetchTeamRoleDescriptions() {
      const { teamId } = this;
      const { data, success } = await getTeamRoleDescriptions({ teamId });

      if (success) {
        this.roleDescriptions = data.items;
      }
    },
    async requestChangeRole(teammateId, role) {
      const { success } = await changeTeammateRole({
        teammateId,
        teamRoleId: role,
      });

      if (success) {
        this.fetchTeammates();
        this.showMessage({
          type: 'success',
          message: 'Teammate role has been updated',
        });
      }
    },
    async requestDeleteTeammate(teammateId) {
      this.disableButton('delete-teammate');
      const { success } = await removeTeammate({ teammateId });
      this.enableButton('delete-teammate');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Teammate has been deleted',
        });

        this.cancelTeammateDelete();
        this.fetchTeammates();
      }
    },
    setData(data) {
      this.teammateArray = data.map((teammate) => {
        const role = this.roleDescriptions.find((role) => role.description === teammate.role);
        const teammateRole = role ? role.value : null;

        return {
          teammateId: teammate.teammateId,
          link: teammate.user.link,
          name: teammate.user.firstName + ' ' + teammate.user.lastName,
          role: teammateRole,
          roleDescription: teammate.role,
          userId: teammate.user.userId,
        };
      });
    },
    handleDeleteTeammate(teammate) {
      this.teammateToDelete = teammate;
      this.isDeleteTeammateConfirmationVisible = true;
    },
    cancelTeammateDelete() {
      this.isDeleteTeammateConfirmationVisible = false;
      this.teammateToDelete = null;
    },
  },
  async created() {
    await Promise.all([this.fetchTeamRoleDescriptions(), this.fetchTeammates()]);
  },
};
</script>

<style lang="scss">
.event-table {
  padding-top: 0;

  overflow: hidden;
  padding: var(--global-dimension-size-125) var(--global-dimension-size-300)
    var(--global-dimension-size-400);
  background-color: var(--color-gray-50);
  border-radius: var(--border-radius-base);

  &__user {
    display: flex;
    align-items: center;
    flex-flow: row nowrap;
  }

  &__avatar {
    margin-right: var(--global-dimension-size-200);
  }

  &__name {
    font-weight: 500;
    font-size: var(--font-size-200);
    line-height: 1.5;
    vertical-align: middle;
    color: var(--color-gray-900);
  }

  &__expiration-date {
    font-size: var(--font-size-100);
    color: var(--color-gray-800);
    font-weight: normal;
  }

  &__comment {
    word-break: normal;
  }

  &__role {
    text-transform: capitalize;
    font-weight: 500;
    font-size: var(--font-size-100);
    line-height: 1.6;
  }

  &__function {
    text-transform: capitalize;
    word-break: break-word;

    & + & {
      position: relative;
      margin-left: 15px;

      &:before {
        position: absolute;
        top: var(--global-dimension-size-85);
        left: -12px;
        content: '';

        width: var(--global-dimension-size-65);
        height: var(--global-dimension-size-65);

        background-color: var(--color-gray-800);
        border-radius: 50%;
      }
    }
  }
}
</style>
